import {Injectable} from '@angular/core';
import {VisbosService} from '../../../core/visbos-core/graphql/visbos.service';
import {Apollo, ApolloQueryObservable} from 'apollo-angular';
import {NgRedux} from '@angular-redux/store';
import {IAppState} from '../../../shared/redux-store';
import {SessionActions} from '../../../shared/actions';
import gql from 'graphql-tag';
import {Observable} from 'rxjs';
import {ApolloQueryResult} from 'apollo-client';
import {
  DeleteInvoiceMutation,
  GenerateInvoiceMutation,
  GenerateInvoiceMutationVariables,
  GenerateInvoicePdfMutation,
  GetInvoiceQuery,
  GetInvoicesQuery,
  UpdateInvoiceMutation,
  UpdateInvoiceMutationVariables
} from '../../../shared/graphql/graphql.types';

export const invoiceFragments = {
  invoices: gql`
    fragment InvoiceFields on Invoice {
      cuid label created_at updated_at

      invoice_no invoice_date delivery_date

      billing_address_name billing_address_street billing_address_city billing_address_country
      invoice_address_name invoice_address_street invoice_address_city invoice_address_country

      invoice_opening_text invoice_payment_terms_text invoice_taxes_text invoice_ending_text

      invoice_items {
        cuid pos_no text
        article_number article_label amount packing_unit tax price_netto total_price
      }

      total_netto total_taxes total_brutto

      administration_unit {cuid label}
    }
  `,
  invoicesOverview: gql`
    fragment InvoiceOverviewFields on Invoice {
      cuid label created_at updated_at is_editable

      invoice_no invoice_date delivery_date

      billing_address_name invoice_address_name

      administration_unit {cuid label}
    }
  `
};

export const generateInvoiceMutation = gql`
  mutation GenerateInvoice($accountingUnitId: String!, $accountingUnitType: String!, $invoiceData: InvoiceInput!, $aggregateData: Boolean, $start: String, $end: String) {
    createInvoice(accounting_unit_id:$accountingUnitId,
      accounting_unit_type: $accountingUnitType,
      invoice_data: $invoiceData,
      aggregate_data: $aggregateData,
      start: $start,
      end: $end) {
      ...InvoiceFields
    }
  }
  ${invoiceFragments.invoices}
`;

export const updateInvoiceMutation = gql`
  mutation UpdateInvoice($invoiceCuid: ID!, $invoiceData: InvoiceInput!) {
    updateInvoice(invoice_cuid: $invoiceCuid, invoice_data: $invoiceData) {
      ...InvoiceFields
    }
  }
  ${invoiceFragments.invoices}
`;

export const generateInvoicePdfMutation = gql`
  mutation GenerateInvoicePdf($invoiceCuid: String!) {
    generateInvoicePdf(cuid: $invoiceCuid)
  }
`;

export const getInvoiceQuery = gql`
  query GetInvoice($cuid:String!) {
    invoice (cuid: $cuid) {
      ...InvoiceFields
    }
  }
  ${invoiceFragments.invoices}
`;

export const getInvoicesQuery = gql`
  query GetInvoices($adminUnitCuid:String) {
    invoices(admin_unit_cuid: $adminUnitCuid) {
      ...InvoiceOverviewFields
    }
  }
  ${invoiceFragments.invoicesOverview}
`;

export const deleteInvoiceMutation = gql`
  mutation DeleteInvoice($invoiceCuid: String!) {
    deleteInvoice(cuid: $invoiceCuid)
  }
`;

@Injectable()
export class InvoiceService extends VisbosService {

  constructor(apollo: Apollo, ngRedux: NgRedux<IAppState>, sessionActions: SessionActions) {
    super(apollo, ngRedux, sessionActions);
  }

  generateInvoice(data: GenerateInvoiceMutationVariables): Observable<ApolloQueryResult<GenerateInvoiceMutation>> {
    return this.mutate({
      mutation: generateInvoiceMutation,
      variables: data,
      refetchQueries: ['GetInvoices']
    });
  }

  getInvoice(cuid): ApolloQueryObservable<GetInvoiceQuery> {
    return this.fetchItems({
      query: getInvoiceQuery,
      variables: {
        cuid
      }
    });
  }

  getInvoices(variables?): ApolloQueryObservable<GetInvoicesQuery> {
    return this.fetchItems({
      query: getInvoicesQuery,
      variables
    });
  }

  updateInvoice(variables: UpdateInvoiceMutationVariables): Observable<ApolloQueryResult<UpdateInvoiceMutation>> {
    return this.mutate({
      mutation: updateInvoiceMutation,
      variables
    });
  }

  generateInvoicePdf(cuid: string): Observable<ApolloQueryResult<GenerateInvoicePdfMutation>> {
    return this.mutate({
      mutation: generateInvoicePdfMutation,
      variables: {
        invoiceCuid: cuid
      }
    });
  }

  deleteInvoice(cuid: string): Observable<ApolloQueryResult<DeleteInvoiceMutation>> {
    return this.mutate({
      mutation: deleteInvoiceMutation,
      variables: {
        invoiceCuid: cuid
      },
      refetchQueries: ['GetInvoices']
    });
  }

}



// WEBPACK FOOTER //
// ./src/app/store-management/services/invoice/invoice.service.ts